'use client';
import {
    getAllPermissions,
    hasAnyPermission,
    hasPermission,
    PermissionAction,
    PermissionContext,
} from '@/lib/permisison/permission';
import { trpc } from '@/providers/trpc-provider';
import { useEffect, useState } from 'react';

interface UserDetails {
    id: string;
    name: string;
    email: string;
    role: string | null;
}

interface OrgRole {
    orgId: string;
    role: string;
}

interface ProjectRole {
    projectId: string;
    role: string | null;
    organizationId: string;
}

interface GroupRole {
    groupId: string;
    role: string | null;
    projectId: string;
    organizationId: string;
}

interface PermissionData {
    appRoles: string[];
    orgRoles: OrgRole[];
    projectRoles: ProjectRole[];
    groupRoles: GroupRole[];
    userDetails: UserDetails | null;
    isLoading: boolean;
    error: string | null;
}

export function usePermission() {
    const [data, setData] = useState<PermissionData>({
        appRoles: [],
        orgRoles: [],
        projectRoles: [],
        groupRoles: [],
        userDetails: null,
        isLoading: true,
        error: null,
    });

    const {
        data: permissionData,
        isLoading,
        error,
    } = trpc.user.getPermissions.useQuery(undefined, {
        staleTime: 2 * 60 * 1000, // 2 minutes
        refetchOnWindowFocus: false,
        refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes instead of on every mount
    });

    useEffect(() => {
        if (permissionData) {
            setData({
                appRoles: (permissionData.appRoles || []).filter(
                    (role): role is string => role !== null,
                ),
                orgRoles: permissionData.orgRoles || [],
                projectRoles: permissionData.projectRoles || [],
                groupRoles: permissionData.groupRoles || [],
                userDetails: permissionData.userDetails || null,
                isLoading: false,
                error: null,
            });
        }
        if (error) {
            setData((prev) => ({
                ...prev,
                isLoading: false,
                error: error.message,
            }));
        }
    }, [permissionData, error, isLoading]);

    const isAppAdmin = (): boolean => {
        return hasAnyPermission('app', data.appRoles, '*' as PermissionAction);
    };

    const checkAppPermission = (action: PermissionAction): boolean => {
        if (isAppAdmin()) return true;
        return hasAnyPermission('app', data.appRoles, action);
    };

    const checkOrgPermission = (
        orgId: string,
        action: PermissionAction,
    ): boolean => {
        if (isAppAdmin()) return true;

        const orgRole = data.orgRoles.find((role) => role.orgId === orgId);
        if (!orgRole) return false;

        return hasPermission('org', orgRole.role, action);
    };

    const checkProjectPermission = (
        projectId: string,
        action: PermissionAction,
    ): boolean => {
        if (isAppAdmin()) return true;

        const projectRole = data.projectRoles.find(
            (role) => role.projectId === projectId,
        );
        if (!projectRole) return false;

        const projectPermissions = projectRole.role
            ? getAllPermissions('project', [projectRole.role])
            : [];
        const orgRole = data.orgRoles.find(
            (role) => role.orgId === projectRole.organizationId,
        );
        const orgPermissions = orgRole
            ? getAllPermissions('org', [orgRole.role])
            : [];

        const allPermissions = [...projectPermissions, ...orgPermissions];

        return allPermissions.includes(action) || allPermissions.includes('*');
    };

    // Helper function to get project and org permissions for a given projectId
    const getProjectAndOrgPermissions = (
        projectId: string,
    ): { projectPermissions: string[]; orgPermissions: string[] } => {
        const projectRole = data.projectRoles.find(
            (role) => role.projectId === projectId,
        );

        const projectPermissions =
            projectRole && projectRole.role
                ? getAllPermissions('project', [projectRole.role])
                : [];

        const orgPermissions = projectRole
            ? (() => {
                  const orgRole = data.orgRoles.find(
                      (role) => role.orgId === projectRole.organizationId,
                  );
                  return orgRole
                      ? getAllPermissions('org', [orgRole.role])
                      : [];
              })()
            : [];

        return { projectPermissions, orgPermissions };
    };

    // Helper function to check if an action is allowed in permissions array
    const hasActionPermission = (
        permissions: string[],
        action: PermissionAction,
    ): boolean => {
        return permissions.includes(action) || permissions.includes('*');
    };

    const checkGroupPermission = (
        groupId: string,
        action: PermissionAction,
        projectId?: string,
    ): boolean => {
        if (isAppAdmin()) return true;

        const groupRole = data.groupRoles.find(
            (role) => role.groupId === groupId,
        );

        // If user has a group role, check group, project, and org permissions
        if (groupRole) {
            const groupPermissions = groupRole.role
                ? getAllPermissions('group', [groupRole.role])
                : [];

            const { projectPermissions, orgPermissions } =
                getProjectAndOrgPermissions(groupRole.projectId);

            const allPermissions = [
                ...groupPermissions,
                ...projectPermissions,
                ...orgPermissions,
            ];

            return hasActionPermission(allPermissions, action);
        }

        // If no group role exists, check project permissions if projectId is provided
        if (projectId) {
            const { projectPermissions, orgPermissions } =
                getProjectAndOrgPermissions(projectId);

            const allPermissions = [...projectPermissions, ...orgPermissions];
            return hasActionPermission(allPermissions, action);
        }

        return false;
    };

    const hasOrgRole = (orgId: string): boolean => {
        return data.orgRoles.some((role) => role.orgId === orgId);
    };

    const hasProjectRole = (projectId: string): boolean => {
        return data.projectRoles.some((role) => role.projectId === projectId);
    };

    const hasGroupRole = (groupId: string): boolean => {
        return data.groupRoles.some((role) => role.groupId === groupId);
    };

    const getOrgRole = (orgId: string): string | null => {
        const orgRole = data.orgRoles.find((role) => role.orgId === orgId);
        return orgRole ? orgRole.role : null;
    };

    const getProjectRole = (projectId: string): string | null => {
        const projectRole = data.projectRoles.find(
            (role) => role.projectId === projectId,
        );
        return projectRole ? projectRole.role : null;
    };

    const getGroupRole = (groupId: string): string | null => {
        const groupRole = data.groupRoles.find(
            (role) => role.groupId === groupId,
        );
        return groupRole ? groupRole.role : null;
    };

    const checkPermission = (
        context: PermissionContext,
        action: PermissionAction,
    ): boolean => {
        if (isAppAdmin()) return true;

        if (context === 'app') {
            return checkAppPermission(action);
        }

        const roles =
            context === 'org'
                ? data.orgRoles
                      .map((role) => role.role)
                      .filter((role): role is string => role !== null)
                : context === 'project'
                  ? data.projectRoles
                        .map((role) => role.role)
                        .filter((role): role is string => role !== null)
                  : data.groupRoles
                        .map((role) => role.role)
                        .filter((role): role is string => role !== null);

        return hasAnyPermission(context, roles, action);
    };

    const checkSpecificPermission = (
        context: PermissionContext,
        role: string,
        action: PermissionAction,
    ): boolean => {
        if (isAppAdmin()) return true;
        return hasPermission(context, role, action);
    };

    const getProjectPermissions = (projectId: string): string[] => {
        if (isAppAdmin()) return ['*'];

        const projectRole = data.projectRoles.find(
            (role) => role.projectId === projectId,
        );
        if (!projectRole) return [];

        const projectPermissions = projectRole.role
            ? getAllPermissions('project', [projectRole.role])
            : [];
        const orgRole = data.orgRoles.find(
            (role) => role.orgId === projectRole.organizationId,
        );
        const orgPermissions = orgRole
            ? getAllPermissions('org', [orgRole.role])
            : [];

        return [...new Set([...projectPermissions, ...orgPermissions])];
    };

    const getGroupPermissions = (groupId: string): string[] => {
        if (isAppAdmin()) return ['*'];

        const groupRole = data.groupRoles.find(
            (role) => role.groupId === groupId,
        );
        if (!groupRole) return [];

        const groupPermissions = groupRole.role
            ? getAllPermissions('group', [groupRole.role])
            : [];

        const projectRole = data.projectRoles.find(
            (role) => role.projectId === groupRole.projectId,
        );
        const projectPermissions =
            projectRole && projectRole.role
                ? getAllPermissions('project', [projectRole.role])
                : [];

        const orgRole = data.orgRoles.find(
            (role) => role.orgId === groupRole.organizationId,
        );
        const orgPermissions = orgRole
            ? getAllPermissions('org', [orgRole.role])
            : [];

        return [
            ...new Set([
                ...groupPermissions,
                ...projectPermissions,
                ...orgPermissions,
            ]),
        ];
    };

    /**
     * Milestone publish affects all groups; only staff may publish.
     * Mirrors {@link ServerPermissions.canPublishMilestoneResults}.
     */
    const canPublishMilestoneResults = (projectId: string): boolean => {
        if (isAppAdmin()) return true;

        const projectRoleEntry = data.projectRoles.find(
            (p) => p.projectId === projectId,
        );
        if (!projectRoleEntry) return false;

        const orgRole = getOrgRole(projectRoleEntry.organizationId);
        if (orgRole === 'owner' || orgRole === 'admin') return true;

        const pr = projectRoleEntry.role;
        if (pr === 'creator' || pr === 'moderator') return true;

        return false;
    };

    /**
     * Check if user can assign/unassign evaluators for a project.
     * Only app admin, org admin, project creator, or project moderator can manage evaluator assignments.
     *
     * @param projectId - The project ID
     * @param organizationId - The organization ID that owns the project
     * @returns true if user has permission to manage evaluator assignments
     */
    const canManageEvaluatorAssignments = (
        projectId: string,
        organizationId: string,
    ): boolean => {
        // Check if user is app admin
        if (isAppAdmin()) return true;

        // Check if user is org admin
        const orgRole = getOrgRole(organizationId);
        if (orgRole === 'admin') return true;

        // Check if user is project creator or moderator
        const projectRole = getProjectRole(projectId);
        if (projectRole === 'creator' || projectRole === 'moderator')
            return true;

        return false;
    };

    return {
        ...data,
        isAppAdmin,
        checkPermission,
        checkSpecificPermission,
        checkAppPermission,
        checkOrgPermission,
        checkProjectPermission,
        checkGroupPermission,
        hasOrgRole,
        hasProjectRole,
        hasGroupRole,
        getOrgRole,
        getProjectRole,
        getGroupRole,
        getProjectPermissions,
        getGroupPermissions,
        canPublishMilestoneResults,
        canManageEvaluatorAssignments,
    };
}
